import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import React, { createContext, useContext, useRef, useState } from 'react';
import { AsyncCourseStaticPriceText, BrowseCourseBadgeHandler, BrowseCourseBadgesContext } from '@udemy/browse-course';
import { BUYABLE_TYPES } from '@udemy/cart-temp-migration';
import { getUniqueId } from '@udemy/design-system-utils';
import { Tracker, trackGenericCourseClick } from '@udemy/event-tracking';
import { useFunnelLogContextStore } from '@udemy/funnel-tracking';
import { useI18n } from '@udemy/i18n';
import { Icon } from '@udemy/icons';
import { Button } from '@udemy/react-core-components';
import { Popover } from '@udemy/react-popup-components';
import { PURCHASE_PRICE_TYPES, SaveToListButton, Wishlist, WishlistStore } from '@udemy/shopping';
import { useUDData } from '@udemy/ud-data';
import { AddToCart } from '../../add-to-cart/add-to-cart.react-component';
import { EnrollNowEvent, QuickViewBoxOpenEvent } from '../../external/events';
import { UI_REGION } from '../../external/ui-regions';
import { CourseObjectivesList } from '../course-objectives/course-objectives-quick-view-box.react-component';
import { useQuickViewBoxFeatures } from '../experimentation-helpers';
import styles from "./course-details-quick-view-box.module.css";
export const CourseDetailsQuickViewBoxContext = /*#__PURE__*/createContext({
  showAiGeneratedHighlights: false
});
const InternalCourseDetailsQuickViewBoxContent = props => {
  var _course$objectives_su;
  const {
    allowAddToCartSuccessModal,
    cartButtonTextGoToCart,
    cartButtonDisableOnGoToCart,
    assessmentRecommendation,
    className,
    course,
    onCourseTitleClickCallbackFunc,
    onEnrollNowClickCallbackFunc,
    resourceContextMenu,
    learningPathId,
    showWishlist,
    showCta,
    popoverContentId,
    hideLearningLinkForUnenrolledPPUsers,
    addToCartContext,
    customBadge
  } = props;
  const {
    Config,
    me,
    request
  } = useUDData();
  const {
    gettext,
    lang
  } = useI18n();
  const [wishlistStore] = useState(new WishlistStore(course, me));
  const funnelLogContextStore = useFunnelLogContextStore();
  const browseBadgeContext = useContext(BrowseCourseBadgesContext);
  const {
    showAiGeneratedHighlights
  } = useContext(CourseDetailsQuickViewBoxContext);

  /**
   * Publishes a `EnrollNowEvent` and executes `onEnrollNowClickCallbackFunc()` if it exists.
   */
  function trackEnrollNowClick() {
    const buyable = {
      id: course.id,
      type: BUYABLE_TYPES.COURSE,
      trackingId: course.frontendTrackingId || course.tracking_id
    };
    Tracker.publishEvent(new EnrollNowEvent({
      buyable
    }));
    if (onEnrollNowClickCallbackFunc) {
      onEnrollNowClickCallbackFunc();
    }
  }

  /**
   * Publishes a `Generic Course Click` and executes `onCourseTitleClickCallbackFunc()` if it exists.
   */
  function trackCourseTitleClick() {
    trackGenericCourseClick({
      courseId: course.id,
      courseTrackingId: course.frontendTrackingId || course.tracking_id,
      componentName: 'quickViewBox'
    });
    if (onCourseTitleClickCallbackFunc) {
      onCourseTitleClickCallbackFunc();
    }
  }
  const getCourseBadges = () => {
    if (!course.is_in_user_subscription) {
      const badgeHandler = new BrowseCourseBadgeHandler(course, browseBadgeContext.browseBadgeHandlerOptions);
      return badgeHandler.getBadgeComponents();
    }
  };
  const isUserEnrolled = course.is_user_subscribed || course.is_in_user_subscription;
  const userLocale = request.locale.replace('_', '-') || 'en-US';
  const courseDate = course.last_update_date ? {
    type: gettext('Updated'),
    date: new Date(course.last_update_date).toLocaleDateString(userLocale, {
      month: 'long',
      year: 'numeric'
    })
  } : {
    type: gettext('Published'),
    date: new Date(course.published_time).toLocaleDateString(userLocale, {
      month: 'long',
      year: 'numeric'
    })
  };
  const captionsList = [];
  if (course.has_closed_caption) {
    captionsList.push(gettext('Subtitles'));
  }
  if (course.has_508_closed_captions) {
    captionsList.push(gettext('CC'));
  }
  const statsLine = /*#__PURE__*/React.createElement("div", {
    className: classNames('ud-text-xs', styles.stats)
  }, course.content_info && /*#__PURE__*/React.createElement("span", null, course.content_info), course.instructional_level && /*#__PURE__*/React.createElement("span", null, course.instructional_level), captionsList && captionsList.length > 0 && /*#__PURE__*/React.createElement("span", null, captionsList.join(', ')));
  const isLimitedConsumptionTrial = Config.brand.has_organization && Config.brand.organization.is_limited_consumption_trial;
  let courseUrl = isUserEnrolled && !hideLearningLinkForUnenrolledPPUsers ? course.learn_url : course.url;
  if (learningPathId) {
    courseUrl = `${courseUrl}?learning_path_id=${learningPathId}`;
  }
  let cta;
  if (isLimitedConsumptionTrial) {
    const [url, queryString] = course.url.split('?');
    const searchParams = new URLSearchParams(queryString);
    searchParams.set('from_quick_view', '1');
    cta = /*#__PURE__*/React.createElement(Button, {
      componentClass: "a",
      "data-testid": "watch-videos",
      href: `${url}?${searchParams.toString()}`,
      udStyle: "brand"
    }, /*#__PURE__*/React.createElement(Icon, {
      glyph: "play",
      label: false
    }), gettext('Watch videos'));
  } else if (assessmentRecommendation || isUserEnrolled) {
    // TODO: href should update after the backend ready
    // assessmentRecommendation: In the new assessment project, we recommend courses.
    // We don't know if the user is enrolled or not in the course
    // We use a generic copy, 'View course' instead of 'Enroll now.'
    cta = /*#__PURE__*/React.createElement(Button, {
      componentClass: "a",
      "data-testid": "view-course",
      href: courseUrl,
      udStyle: "brand"
    }, gettext('View course'));
  } else if (course.free_course_subscribe_url) {
    cta = /*#__PURE__*/React.createElement(Button, {
      componentClass: "a",
      "data-testid": "enroll-now-button",
      href: course.free_course_subscribe_url,
      udStyle: Config.brand.has_organization ? 'brand' : 'primary',
      onClick: trackEnrollNowClick
    }, gettext('Enroll now'));
  } else {
    cta = /*#__PURE__*/React.createElement(AddToCart, {
      allowAddToCartSuccessModal: allowAddToCartSuccessModal,
      cartButtonTextGoToCart: cartButtonTextGoToCart,
      cartButtonDisableOnGoToCart: cartButtonDisableOnGoToCart,
      buyables: [course],
      addToCartContext: addToCartContext
    });
  }
  const courseContextMenu = resourceContextMenu !== null && resourceContextMenu !== void 0 && resourceContextMenu.getQuickViewBoxContextMenu ? resourceContextMenu.getQuickViewBoxContextMenu({
    ...course,
    isPublished: true
  }) : null;
  const uiRegion = funnelLogContextStore !== null && funnelLogContextStore !== void 0 && funnelLogContextStore.subcontext ? `${funnelLogContextStore === null || funnelLogContextStore === void 0 ? void 0 : funnelLogContextStore.subcontext.replace(/ +/g, '_')}.${UI_REGION.QUICK_PREVIEW}` : UI_REGION.QUICK_PREVIEW;
  const badges = getCourseBadges();
  return /*#__PURE__*/React.createElement("div", {
    className: className
  }, /*#__PURE__*/React.createElement("a", {
    className: classNames('ud-heading-lg', styles.title),
    href: courseUrl,
    "data-testid": "quick-view-box-title",
    onClick: trackCourseTitleClick,
    onContextMenu: trackCourseTitleClick,
    id: popoverContentId
  }, course.title), /*#__PURE__*/React.createElement("div", {
    "data-testid": "badge-container",
    className: classNames(styles['badge-container'], {
      [styles['column-view']]: badges && badges.length > 1
    })
  }, customBadge, badges && badges.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: styles['badges']
  }, badges), /*#__PURE__*/React.createElement("span", {
    className: classNames('ud-text-xs', styles.updated)
  }, courseDate.type, /*#__PURE__*/React.createElement("span", {
    className: "ud-heading-xs"
  }, " ", courseDate.date))), courseContextMenu && /*#__PURE__*/React.createElement("div", {
    className: styles['context-menu']
  }, courseContextMenu), showAiGeneratedHighlights && course.ai_generated_highlights ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: classNames(styles['genai-heading'], 'ud-heading-sm')
  }, /*#__PURE__*/React.createElement(Icon, {
    className: styles['genai-icon'],
    glyph: "sparkles",
    size: 'small',
    color: "inherit",
    label: false
  }), gettext('Course summary')), /*#__PURE__*/React.createElement("p", {
    className: "ud-text-sm"
  }, course.ai_generated_highlights.summary)), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: classNames(styles['genai-heading'], 'ud-heading-sm')
  }, gettext('Skills you will learn')), /*#__PURE__*/React.createElement("p", {
    className: "ud-text-sm"
  }, new Intl.ListFormat(lang, {
    style: 'narrow',
    type: 'conjunction'
  }).format(course.ai_generated_highlights.learning_outcomes)))) : /*#__PURE__*/React.createElement(React.Fragment, null, statsLine, /*#__PURE__*/React.createElement("div", {
    className: classNames('ud-text-sm', styles.headline),
    "data-testid": "quick-view-box-headline"
  }, course.headline), /*#__PURE__*/React.createElement("div", {
    className: classNames('ud-text-sm', styles.objectives, {
      [styles['no-cta']]: !showCta
    })
  }, (_course$objectives_su = course.objectives_summary) !== null && _course$objectives_su !== void 0 && _course$objectives_su.length ? /*#__PURE__*/React.createElement(CourseObjectivesList, {
    objectives: course.objectives_summary
  }) : gettext('No course details available'))), props.showPrice && /*#__PURE__*/React.createElement(AsyncCourseStaticPriceText, {
    buyables: [course],
    trackingEventContext: {
      buyableId: course.id,
      priceType: PURCHASE_PRICE_TYPES.individual_buyable,
      buyableType: 'course',
      buyableTrackingId: course.tracking_id
    }
  }), showCta && /*#__PURE__*/React.createElement("div", {
    className: styles.cta
  }, /*#__PURE__*/React.createElement("div", {
    className: styles['add-to-cart']
  }, cta), showWishlist && course.is_paid && !isUserEnrolled && /*#__PURE__*/React.createElement("div", {
    className: styles['cta-button']
  }, /*#__PURE__*/React.createElement(Wishlist, {
    wishlistStore: wishlistStore,
    round: true,
    size: "large",
    uiRegion: uiRegion
  })), course.is_in_user_subscription && /*#__PURE__*/React.createElement("div", {
    className: styles['cta-button']
  }, /*#__PURE__*/React.createElement(SaveToListButton, {
    course: course,
    round: true,
    size: "large",
    uiRegion: uiRegion
  }))));
};
export const CourseDetailsQuickViewBoxContent = Object.assign(inject(({
  resourceContextMenu
}) => ({
  resourceContextMenu
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}))(({
  resourceContextMenu,
  ...rest
}) => {
  return (
    /*#__PURE__*/
    // @ts-expect-error ...rest is not adequately typed
    React.createElement(InternalCourseDetailsQuickViewBoxContent, Object.assign({}, rest, {
      resourceContextMenu: resourceContextMenu
    }))
  );
}), {
  displayName: 'CourseDetailsQuickViewBoxContent'
});

/**
 * Props for the `CourseDetailsQuickViewBox` component
 */

/**
 * Renders the course details in a popover that is triggered on hover and attached to the given
 * `courseCard` component
 */
export const CourseDetailsQuickViewBox = Object.assign(observer(props => {
  var _props$placement;
  const {
    allowAddToCartSuccessModal,
    cartButtonTextGoToCart,
    cartButtonDisableOnGoToCart,
    className,
    course,
    courseCard,
    showCta,
    onCourseTitleClickCallbackFunc,
    onEnrollNowClickCallbackFunc,
    addToCartContext,
    contentId,
    contentRole,
    customBadge
  } = props;
  const {
    gettext
  } = useI18n();
  const funnelLogContextStore = useFunnelLogContextStore();
  const popoverRef = useRef(null);
  const triggerButtonRef = useRef(null);
  const [popoverContentId] = useState(getUniqueId('popover-content'));
  const [isTriggeredViaButton, setIsTriggeredViaButton] = useState(false);
  const showPrice = !!props.showPrice;
  const placement = (_props$placement = props.placement) !== null && _props$placement !== void 0 ? _props$placement : 'right';
  const {
    showWishlist: showWishlistProp
  } = props;
  const {
    personalPlanExplicitEnrollFeature
  } = useQuickViewBoxFeatures();
  const {
    me: udMe
  } = useUDData();

  /**
   * TODO: add default value to the showWishlist prop when the feature is fully rolled out
   * With the new feature, which hides the wishlist button on the quick view box for visitors, we need to add check
   * to see if user is authenticated or not.
   *
   * We could have add a visitor check to hook as well but I think it's better to keep it here since we'll need it
   * when we want to clean up the experimentation.
   */
  const showWishlist = showWishlistProp !== null && showWishlistProp !== void 0 ? showWishlistProp : udMe.is_authenticated;
  function onCloseButtonClick() {
    var _Popper$ref, _triggerButtonRef$cur;
    const Popper = popoverRef.current;
    const BasicPopper = Popper === null || Popper === void 0 || (_Popper$ref = Popper.ref) === null || _Popper$ref === void 0 ? void 0 : _Popper$ref.current;
    BasicPopper === null || BasicPopper === void 0 || BasicPopper.onClose();
    (_triggerButtonRef$cur = triggerButtonRef.current) === null || _triggerButtonRef$cur === void 0 || _triggerButtonRef$cur.focus();
    setIsTriggeredViaButton(false);
  }
  function onTriggerButtonClick(_event) {
    var _Popper$ref2;
    const Popper = popoverRef.current;
    const BasicPopper = Popper === null || Popper === void 0 || (_Popper$ref2 = Popper.ref) === null || _Popper$ref2 === void 0 ? void 0 : _Popper$ref2.current;
    setTimeout(() => BasicPopper === null || BasicPopper === void 0 ? void 0 : BasicPopper.onFocusOpen(document.getElementById(popoverContentId)));
    setTimeout(() => {
      var _document$getElementB;
      return (_document$getElementB = document.getElementById(popoverContentId)) === null || _document$getElementB === void 0 ? void 0 : _document$getElementB.focus({
        preventScroll: true
      });
    });
    setIsTriggeredViaButton(true);
  }
  function onToggle(isOpen) {
    /**
     * Since users can open via button but close via clicking outside, we need to handle this case
     */
    if (!isOpen && isTriggeredViaButton) {
      setIsTriggeredViaButton(false);
    }
    if (props.onToggle) {
      props.onToggle(isOpen);
    }
  }

  //(selectors: PopperGetTabOrderElementSelectors) => ForcedTabOrder[]
  function getTabOrder(selectors) {
    const {
      findFirstFocusableInContent,
      findLastFocusableInContent
    } = selectors;

    // Focus in the popover should be cyclic, so that when we tab off the last element,
    // we go back to the first element and vice versa.
    return [[findLastFocusableInContent, findFirstFocusableInContent], [findLastFocusableInContent, findFirstFocusableInContent]];
  }
  function logQuickViewPreview() {
    funnelLogContextStore.logAction('quick-view-previewed', [{
      id: course.id
    }]);
    Tracker.publishEvent(new QuickViewBoxOpenEvent({
      id: course.id,
      trackingId: course.frontendTrackingId || course.tracking_id
    }));
  }
  function onPopoverFirstOpen() {
    logQuickViewPreview();
  }

  /**
   * Renders popover content using a Popover function
   */
  const renderContent = ({
    className,
    ...props
  }, ...rest) => {
    var _Popover$defaultProps, _Popover$defaultProps2;
    const mergedProps = {
      className: classNames(className, styles['popover-wrapper']),
      ...props,
      'data-testid': 'popover-render-content'
    };
    return (_Popover$defaultProps = (_Popover$defaultProps2 = Popover.defaultProps).renderContent) === null || _Popover$defaultProps === void 0 ? void 0 : _Popover$defaultProps.call(_Popover$defaultProps2, mergedProps, ...rest);
  };
  const popoverContent = /*#__PURE__*/React.createElement("div", {
    "data-testid": "course-details-content"
  }, /*#__PURE__*/React.createElement(CourseDetailsQuickViewBoxContent, {
    allowAddToCartSuccessModal: allowAddToCartSuccessModal !== null && allowAddToCartSuccessModal !== void 0 ? allowAddToCartSuccessModal : true,
    cartButtonTextGoToCart: cartButtonTextGoToCart,
    cartButtonDisableOnGoToCart: cartButtonDisableOnGoToCart,
    course: course,
    className: className,
    showCta: showCta !== null && showCta !== void 0 ? showCta : true,
    showPrice: showPrice,
    showWishlist: showWishlist,
    funnelLogContextStore: funnelLogContextStore,
    onEnrollNowClickCallbackFunc: onEnrollNowClickCallbackFunc,
    onCourseTitleClickCallbackFunc: onCourseTitleClickCallbackFunc,
    popoverContentId: popoverContentId,
    hideLearningLinkForUnenrolledPPUsers: personalPlanExplicitEnrollFeature,
    addToCartContext: addToCartContext,
    customBadge: customBadge
  }), /*#__PURE__*/React.createElement(Button, {
    className: classNames('ud-link-underline', styles['popover-interaction-btn'], {
      'ud-sr-only': !isTriggeredViaButton
    }),
    "data-testid": "close-course-details-popover",
    udStyle: "ghost",
    size: "xsmall",
    onClick: onCloseButtonClick
  }, /*#__PURE__*/React.createElement("span", null, gettext('Close dialog'))));
  const popoverTrigger = /*#__PURE__*/React.createElement("div", {
    "data-testid": "course-details-popover-trigger",
    className: styles['full-height'],
    tabIndex: -1
  }, courseCard, /*#__PURE__*/React.createElement(Button, {
    className: classNames('ud-link-underline', 'ud-sr-only', styles['screen-reader-only'], styles['popover-interaction-btn']),
    "data-testid": "open-course-details-popover",
    udStyle: "ghost",
    size: "xsmall",
    forwardedRef: triggerButtonRef,
    onClick: onTriggerButtonClick
  }, /*#__PURE__*/React.createElement("span", null, gettext('Show course details'))));
  return /*#__PURE__*/React.createElement(Popover, {
    placement: placement,
    trigger: popoverTrigger,
    canToggleOnHover: true,
    canOnlyToggleOnHover: true,
    detachFromTarget: true,
    toggleStrategy: "add-remove",
    onFirstOpen: onPopoverFirstOpen,
    renderContent: renderContent,
    ref: popoverRef,
    getTabOrder: getTabOrder,
    className: styles['full-height'],
    onToggle: onToggle
  }, /*#__PURE__*/React.createElement("div", {
    "data-testid": "popover-content-wrapper",
    id: contentId,
    role: contentRole
  }, popoverContent));
}), {
  displayName: 'CourseDetailsQuickViewBox'
});
//# sourceMappingURL=course-details-quick-view-box.react-component.js.map